/**
 * EmployeeList Stav / my_status — canonical raw tokens + legacy mask normalization.
 * Display labels stay translated (SK/EN/DE); internal filter tokens stay locale-stable.
 */

export const EMPLOYEE_STATUS_ACTIVE = 'active';
export const EMPLOYEE_STATUS_INACTIVE = 'inactive';

/** @type {Readonly<Record<string, 'active'|'inactive'>>} */
const LEGACY_STATUS_MAP = Object.freeze({
  active: EMPLOYEE_STATUS_ACTIVE,
  inactive: EMPLOYEE_STATUS_INACTIVE,
  aktívny: EMPLOYEE_STATUS_ACTIVE,
  aktivny: EMPLOYEE_STATUS_ACTIVE,
  neaktívny: EMPLOYEE_STATUS_INACTIVE,
  neaktivny: EMPLOYEE_STATUS_INACTIVE,
  // EN display labels accidentally stored as raw
  // (pre-Phase-C EN locale used t(Status*) as value)
  // DE Aktiv / Inaktiv
  aktiv: EMPLOYEE_STATUS_ACTIVE,
  inaktiv: EMPLOYEE_STATUS_INACTIVE,
});

/**
 * Normalize one status token from Multiselect / saved mask to canonical raw.
 * Unknown tokens return null (do not invent a match).
 *
 * @param {unknown} raw
 * @returns {'active'|'inactive'|null}
 */
export function normalizeEmployeeStatusFilterValue(raw) {
  if (raw == null) return null;
  const s = String(raw).trim();
  if (!s) return null;
  const key = s.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
  // Map without diacritics first (aktivny / neaktivny / aktiv / inaktiv / active / inactive)
  if (key === 'active') return EMPLOYEE_STATUS_ACTIVE;
  if (key === 'inactive') return EMPLOYEE_STATUS_INACTIVE;
  if (key === 'aktivny') return EMPLOYEE_STATUS_ACTIVE;
  if (key === 'neaktivny') return EMPLOYEE_STATUS_INACTIVE;
  if (key === 'aktiv') return EMPLOYEE_STATUS_ACTIVE;
  if (key === 'inaktiv') return EMPLOYEE_STATUS_INACTIVE;
  // Fallback exact (pre-normalized) map for safety
  const folded = s.toLowerCase();
  return LEGACY_STATUS_MAP[folded] ?? null;
}

/**
 * Canonical row status from date_of_departure (authoritative).
 * @param {object|null|undefined} rowData
 * @returns {'active'|'inactive'}
 */
export function employeeRowStatusRaw(rowData) {
  const dep = rowData?.date_of_departure;
  return dep === '' || dep == null ? EMPLOYEE_STATUS_ACTIVE : EMPLOYEE_STATUS_INACTIVE;
}

/**
 * Parse Multiselect / Tabulator header value into raw status tokens (may be legacy).
 * @param {unknown} headerValue
 * @returns {string[]}
 */
export function parseEmployeeStatusHeaderMulti(headerValue) {
  if (headerValue == null || headerValue === '') return [];
  if (Array.isArray(headerValue)) {
    return headerValue.map((x) => String(x ?? '').trim()).filter(Boolean);
  }
  if (typeof headerValue === 'object' && Array.isArray(headerValue?.value)) {
    return headerValue.value.map((x) => String(x ?? '').trim()).filter(Boolean);
  }
  return String(headerValue)
    .split(/[\s,;|]+/)
    .map((x) => x.trim())
    .filter(Boolean);
}

/**
 * Normalize a list of selected status tokens to canonical active/inactive.
 * Drops unknown tokens.
 * @param {unknown} headerValue
 * @returns {Array<'active'|'inactive'>}
 */
export function normalizeEmployeeStatusSelection(headerValue) {
  const out = [];
  const seen = new Set();
  for (const token of parseEmployeeStatusHeaderMulti(headerValue)) {
    const n = normalizeEmployeeStatusFilterValue(token);
    if (!n || seen.has(n)) continue;
    seen.add(n);
    out.push(n);
  }
  return out;
}

/**
 * Tabulator headerFilterFunc for EmployeeList my_status.
 * @param {unknown} headerValue
 * @param {unknown} _rowValue
 * @param {object} rowData
 * @returns {boolean}
 */
export function employeeStatusHeaderFilter(headerValue, _rowValue, rowData) {
  const selected = normalizeEmployeeStatusSelection(headerValue);
  if (selected.length === 0) return true;
  return selected.includes(employeeRowStatusRaw(rowData));
}

/**
 * Normalize my_status values inside a mask filters JSON (array or string).
 * Runtime only — does not write to DB.
 * @param {unknown} filters
 * @returns {unknown} same shape; my_status values canonicalized when recognized
 */
export function normalizeEmployeeMaskFilters(filters) {
  const parsed = typeof filters === 'string'
    ? (() => {
        try {
          return JSON.parse(filters);
        } catch (_) {
          return filters;
        }
      })()
    : filters;
  if (!Array.isArray(parsed)) return filters;
  return parsed.map((f) => {
    if (!f || f.field !== 'my_status') return f;
    const selected = normalizeEmployeeStatusSelection(f.value);
    if (selected.length === 0) return { ...f, value: f.value ?? '' };
    return { ...f, value: selected.length === 1 ? selected[0] : selected.join(',') };
  });
}

/**
 * Normalize filter_my_status export param to canonical token when possible.
 * @param {unknown} value
 * @returns {string} canonical active|inactive or original trimmed string
 */
export function canonicalizeEmployeeStatusExportParam(value) {
  const selected = normalizeEmployeeStatusSelection(value);
  if (selected.length === 1) return selected[0];
  if (selected.length > 1) return selected.join(',');
  return value == null ? '' : String(value).trim();
}
